import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
} from "@material-ui/core";
import { Auth, Storage, API } from "aws-amplify";
import { nanoid } from "nanoid";
import CustomOverlay from "../../components/CustomOverlay";
import FormProduct from "../../components/forms/product";
import CardProduct from "../../components/cards/product";
import SocialSharing from "../../components/socialSharing";
import Default from "../../layouts/default";

const CreateProduct = () => {
  const [overlay, setOverlay] = useState(false);
  const [showInput, setShowInput] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showThxPage, setShowThxPage] = useState(false);
  const [stripeID, setStripeID] = useState(null);
  const [isStripeChecked, setIsStripeChecked] = useState(false);
  const [showSellerRegistrationBtn, setShowSellerRegistrationBtn] = useState(false);
  const [productForm, setProductForm] = useState({
    UUID: nanoid(),
    stripeID: "",
    title: "",
    URL: [],
    description: "",
    rawPrice: 300,
    feeRate: 0.036,
    platformFee: 200,
    mailtext: "",
    note: "",
    questions: [],
    imgURL: null,
    imgFile: null,
    imgType: null,
    imgPreview: null,
    quantity: null,
  });

  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [origin] = useState(window.location.origin);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        setCurrentUserInfo(user);
        const identityId = user.attributes.sub;

        const result = await API.graphql({
          query: `query GetSeller($id: ID!) {
            getSeller(id: $id) { stripeID }
          }`,
          variables: { id: identityId },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        if (!result.data.getSeller) {
          // 出品者情報が存在しない場合
          setShowSellerRegistrationBtn(true);
        } else {
          // 出品者情報が存在する場合
          const id = result.data.getSeller.stripeID || null;
          setStripeID(id);
        }
      })
      .catch(() => {})
      .finally(() => setIsStripeChecked(true));
  }, []);

  const goToConfirm = () => {
    setShowInput(false);
    setShowConfirm(true);
  };

  const backToInput = () => {
    setShowConfirm(false);
    setShowInput(true);
  };

  const createProduct = async () => {
    if (!currentUserInfo) return alert("ログインしてください。");

    setOverlay(true);

    try {
      const identityId = currentUserInfo.attributes.sub;
      const result = await API.graphql({
        query: `query GetSeller($id: ID!) {
                  getSeller(id: $id) { stripeID }
                }`,
        variables: { id: identityId },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const stripeID = result.data?.getSeller?.stripeID || null;
      productForm.imgFile ? await S3Upload(stripeID) : await createProductAmplify(undefined, stripeID);
    } catch (error) {
      console.error(error);
      alert("商品の作成に失敗しました。時間を置いてから再度お試しください。");
      setOverlay(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const getMimeType = (fileName) => {
      const ext = fileName.split(".").pop().toLowerCase();
      return {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
      }[ext] || "image/png";
    };

    const imgType = selectedFile.type || getMimeType(selectedFile.name);
    const imgURL = URL.createObjectURL(selectedFile);

    setProductForm((prev) => ({
      ...prev,
      imgFile: selectedFile,
      imgType,
      imgPreview: imgURL,
    }));
  };

  const S3Upload = async (stripeID) => {
    if (!productForm.imgFile) return await createProductAmplify(undefined, stripeID);

    try {
      const credentials = await Auth.currentCredentials();
      const imgExt = productForm.imgType?.split("/")[1] || "jpeg";
      const key = `image-product/${productForm.UUID}.${imgExt}`;

      await Storage.put(key, productForm.imgFile, {
        level: "public",
        contentType: productForm.imgType,
        metadata: { identityId: credentials.identityId },
      });

      setProductForm((prev) => ({ ...prev, imgURL: key }));
      await createProductAmplify(key, stripeID);
    } catch (error) {
      alert(`画像のアップロードに失敗しました。\n詳細: ${error.message}`);
      setOverlay(false);
    }
  };

  const createProductAmplify = async (imgURL, stripeID) => {
    try {
      const idToken = (await Auth.currentSession()).getIdToken().getJwtToken();
      const identityId = currentUserInfo.attributes.sub;

      const input = {
        id: productForm.UUID,
        title: productForm.title || "No title",
        sellerID: identityId,
        stripeID: stripeID || null,
        URL: productForm.URL.filter(Boolean),
        description: productForm.description || null,
        price: productForm.rawPrice || 0,
        mailtext: productForm.mailtext || "",
        note: productForm.note || "",
        form: productForm.questions || [],
        imgURL: imgURL || productForm.imgURL || null,
        identityId,
        quantity: productForm.quantity || 1,
      };

      await API.graphql({
        query: `mutation CreateProduct($input: CreateProductInput!) {
                  createProduct(input: $input) {
                    id imgURL quantity
                  }
                }`,
        authMode: "AMAZON_COGNITO_USER_POOLS",
        variables: { input },
        headers: { Authorization: idToken },
      });

      setShowConfirm(false);
      setShowThxPage(true);
      setOverlay(false);
    } catch (error) {
      console.error("GraphQL エラー詳細:", error);
      alert(`商品の作成に失敗しました。\n詳細: ${error.message}`);
      setOverlay(false);
    }
  };
  
  if (!isStripeChecked) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography variant="body1">確認中...</Typography>
      </Box>
    );
  }
  
  if (showSellerRegistrationBtn) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography variant="h5" gutterBottom>
          出品者情報の登録が必要です
        </Typography>
        <Typography variant="body1" style={{ marginBottom: "16px" }}>
          商品を作成するためには、出品者情報の登録を行ってください。
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.href = "/profile/seller/new"}
        >
          出品者情報を登録する
        </Button>
      </Box>
    );
  }
  
  if (!stripeID) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography variant="h5" gutterBottom>
          Stripe連携が必要です
        </Typography>
        <Typography variant="body1" style={{ marginBottom: "16px" }}>
          Stripeとの連携が必要です。下記ボタンから連携を行ってください。
        </Typography>
        <a
          href="https://connect.stripe.com/oauth/authorize?response_type=code&client_id=ca_HMjw0lVlIUzcj0RMFSgE5sfRkizhNDsC&scope=read_write"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/image/stripe_manual/ConnectStripe.png"
            alt="Stripeと連携"
            style={{ maxWidth: "300px" }}
          />
        </a>
      </Box>
    );
  }
  
  return (
    <Default showFooter={false} noInnerBox={true}>
      <CustomOverlay overlay={overlay} />

      {showInput && (
        <Box className="text-center">
          <Typography variant="h5" align="center" gutterBottom>
            商品の作成
          </Typography>
          <FormProduct
            productForm={productForm}
            setProductForm={setProductForm}
            validateBtn="内容確認"
            onValidate={goToConfirm}
            handleFileChange={handleFileChange}
          />
        </Box>
      )}

      {showConfirm && (
        <Box className="text-center">
          <CardProduct productForm={productForm} />
          <Divider style={{ margin: "16px 0" }} />
          <Grid container justifyContent="center">
            <Button variant="contained" color="default" onClick={backToInput} style={{ margin: "8px" }}>
              戻る
            </Button>
            <Button variant="contained" color="primary" onClick={createProduct} style={{ margin: "8px" }}>
              商品作成
            </Button>
          </Grid>
        </Box>
      )}

      {showThxPage && (
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            商品を作成しました。商品ページは以下のURLになります。
          </Typography>
          <Typography variant="body1">
            <a
              href={`https://${window.location.host}/products/${productForm.UUID}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`https://${window.location.host}/products/${productForm.UUID}`}
            </a>
          </Typography>
          <SocialSharing title={productForm.title} url={`${origin}/products/${productForm.UUID}`} />
          <Typography variant="h6" style={{ marginTop: "16px" }}>
            ご利用ありがとうございました。
          </Typography>
        </Box>
      )}
    </Default>
  );
};

export default CreateProduct;
